import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_ACCESS_TOKEN =
  'pk.eyJ1IjoiZGF2aWQtcGV0dGlmb3ItbmQiLCJhIjoiY200dDNjbWtpMDdlZjJqcHM1ODhkOWF4OSJ9.PQL7pNkiko1SbYsmNrvacQ';

interface MapVizProps {
  height: string;
  width: string;
  zoom: number;
  center: [number, number];
  tilt?: number;
  bearing?: number;
  selectedNetworkType: string;
  children: ((map: mapboxgl.Map) => React.ReactNode) | React.ReactNode;
  isLoading?: boolean;
  onMapLoad?: (map: mapboxgl.Map) => void;
  baseLayer?: 'light' | 'satellite';
  onBaseLayerChange?: (layer: 'light' | 'satellite') => void;
}

export default function MapViz({
  height,
  width,
  zoom,
  center,
  tilt = 0,
  bearing = 0,
  selectedNetworkType,
  children,
  isLoading = false,
  onMapLoad,
  baseLayer = 'light',
  onBaseLayerChange,
}: MapVizProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!mapContainer.current) return;

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    const initialStyle =
      baseLayer === 'satellite'
        ? 'mapbox://styles/mapbox/satellite-streets-v12'
        : 'mapbox://styles/mapbox/light-v11';

    const initializeMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: initialStyle,
      center: [center[1], center[0]],
      zoom: zoom,
      attributionControl: true,
    });

    initializeMap.on('load', () => {
      map.current = initializeMap;
      if (tilt) {
        map.current.setPitch(tilt);
      }
      if (bearing) {
        map.current.setBearing(bearing);
      }
      setMapLoaded(true);
      if (onMapLoad) {
        onMapLoad(initializeMap);
      }
    });

    initializeMap.on('styledata', () => {
      if (map.current) {
        setMapLoaded(true);
        if (onMapLoad) {
          onMapLoad(map.current);
        }
      }
    });

    // add map scale
    initializeMap.addControl(new mapboxgl.ScaleControl(), 'bottom-right');

    // add map zoom control without rotation icon
    initializeMap.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      'top-left',
    );

    // add basemap toggle control
    class BasemapToggleControl {
      _map: mapboxgl.Map | null = null;
      _container: HTMLElement | null = null;
      onAdd(map: mapboxgl.Map) {
        this._map = map;
        this._container = document.createElement('div');
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
        this._container.innerHTML =
          '<button class="mapboxgl-ctrl-icon" style="background-image: url(\'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23333" width="18px" height="18px"%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3Cpath d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9.27l-9-7-9 7 1.63 1.27L12 16z"/%3E%3C/svg%3E\');" title="Toggle basemap"></button>';
        this._container.onclick = () => {
          const currentStyleName = this._map?.getStyle().name;
          const newLayer =
            currentStyleName === 'Mapbox Light' ? 'satellite' : 'light';
          const style =
            newLayer === 'satellite'
              ? 'mapbox://styles/mapbox/satellite-streets-v12'
              : 'mapbox://styles/mapbox/light-v11';
          if (this._map) {
            this._map.setStyle(style);
          }
          if (onBaseLayerChange) {
            onBaseLayerChange(newLayer);
          }
        };
        return this._container;
      }

      onRemove() {
        if (this._container && this._container.parentNode) {
          this._container.parentNode.removeChild(this._container);
        }
        this._map = null;
      }
    }

    initializeMap.addControl(new BasemapToggleControl(), 'top-left');

    return () => {
      initializeMap.remove();
    };
  }, [center, zoom]);

  return (
    <div style={{ width, height, position: 'relative' }}>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      {mapLoaded && map.current && (
        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              bottom: '24px',
              left: '24px',
              background: 'rgba(255, 255, 255, 0.9)',
              padding: '12px',
              borderRadius: '4px',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            }}
          >
            {typeof children === 'function' ? children(map.current) : children}
          </div>
        </div>
      )}
    </div>
  );
}
